import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, Trash2, Edit3, Plus, Mail, Loader2, Filter, X,
  CheckCircle2, AlertTriangle, Shield, UserPlus,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  subscribed:   'bg-emerald-100 text-emerald-700',
  pending:      'bg-slate-100 text-slate-600',
  unsubscribed: 'bg-slate-200 text-slate-600',
  suppressed:   'bg-rose-100 text-rose-700',
  bounced:      'bg-amber-100 text-amber-700',
  complained:   'bg-rose-100 text-rose-700',
};

/**
 * ContactsPanel — audience management inside the DCCF Communication Center.
 * Reusable from EmailCommunicationsPage as the "Audience / Contacts" tab.
 */
export const ContactsPanel = ({ summary, lists, onReload }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [listId, setListId] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [page, setPage] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: page * pageSize };
      if (search.trim())  params.search = search.trim();
      if (status)         params.status = status;
      if (source)         params.source = source;
      if (listId)         params.list_id = listId;
      if (tagFilter.trim()) params.tag = tagFilter.trim();
      const { data } = await axios.get(`${API}/admin/contacts`, { params, withCredentials: true });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load contacts');
    } finally {
      setLoading(false);
    }
  }, [search, status, source, listId, tagFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete contact ${c.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/contacts/${c.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      toast.success('Contact removed');
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="contacts-panel">
      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          <StatTile label="Total contacts" value={summary.total} accent />
          <StatTile label="Subscribed" value={summary.subscribed} />
          <StatTile label="Pending" value={summary.pending} />
          <StatTile label="Unsubscribed" value={summary.unsubscribed} />
          <StatTile label="Suppressed" value={summary.suppressed_or_bounced} warn />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <Button onClick={() => setAddOpen(true)} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="contact-add-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add contact
        </Button>
        <Button onClick={() => setBulkOpen(true)} variant="outline" data-testid="contact-bulk-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add multiple
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search name, email, company…"
            className="pl-9"
            data-testid="contacts-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="h-9 border border-slate-200 rounded-md text-[12.5px] px-2 bg-white"
          data-testid="contacts-status-filter"
        >
          <option value="">All statuses</option>
          <option value="subscribed">Subscribed</option>
          <option value="pending">Pending</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="suppressed">Suppressed</option>
          <option value="bounced">Bounced</option>
        </select>
        <select
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(0); }}
          className="h-9 border border-slate-200 rounded-md text-[12.5px] px-2 bg-white"
          data-testid="contacts-source-filter"
        >
          <option value="">All sources</option>
          <option value="manual">Manual</option>
          <option value="member">Member</option>
          <option value="csv_import">CSV import</option>
          <option value="xlsx_import">Excel import</option>
          <option value="pdf_import">PDF import</option>
          <option value="historical">Historical</option>
        </select>
        <select
          value={listId}
          onChange={(e) => { setListId(e.target.value); setPage(0); }}
          className="h-9 border border-slate-200 rounded-md text-[12.5px] px-2 bg-white max-w-[160px]"
          data-testid="contacts-list-filter"
        >
          <option value="">All lists</option>
          {(lists || []).map((l) => (
            <option key={l.id} value={l.id}>{l.name} ({l.count})</option>
          ))}
        </select>
        <Input
          value={tagFilter}
          onChange={(e) => { setTagFilter(e.target.value); setPage(0); }}
          placeholder="Filter by tag"
          className="w-[140px] h-9"
          data-testid="contacts-tag-filter"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="contacts-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Email</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Name</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Company</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Status</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Source</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Lists</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No contacts match your filters.</td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 align-top" data-testid={`contact-row-${c.id}`}>
                  <td className="px-3 py-2 font-mono text-[12px] text-[#002249]">
                    {c.email}
                    {c.user_id && (
                      <span className="ml-1.5 text-[9.5px] font-bold px-1 py-0.5 rounded bg-[#002249] text-white uppercase">Member</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{[c.first_name, c.last_name].filter(Boolean).join(' ') || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-slate-600">{c.company || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded ${STATUS_COLOR[c.status] || 'bg-slate-100 text-slate-600'}`}>
                      {(c.status || '').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">{c.source?.replace('_', ' ') || '—'}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {(c.list_ids || []).length > 0
                      ? (c.list_ids || []).slice(0, 2).map(id => {
                          const list = (lists || []).find(l => l.id === id);
                          return list?.name || id.slice(0,6);
                        }).join(', ') + ((c.list_ids || []).length > 2 ? ` +${c.list_ids.length - 2}` : '')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(c)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Edit" data-testid={`contact-edit-${c.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(c)} className="p-1 rounded hover:bg-rose-50 text-rose-500" title="Delete" data-testid={`contact-delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-[11.5px]">
          <div className="text-slate-500">
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total.toLocaleString()}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="contacts-prev">
              Previous
            </Button>
            <span className="text-slate-500 px-2">Page {page + 1} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="contacts-next">
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add single */}
      {addOpen && (
        <AddContactModal
          lists={lists}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); onReload?.(); }}
        />
      )}
      {bulkOpen && (
        <BulkAddModal
          lists={lists}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); load(); onReload?.(); }}
        />
      )}
      {editing && (
        <EditContactModal
          contact={editing}
          lists={lists}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onReload?.(); }}
        />
      )}
    </div>
  );
};

// ─── Pieces ────────────────────────────────────────────────────────

const StatTile = ({ label, value, accent, warn }) => (
  <div className={`rounded-lg px-3 py-2 border ${accent ? 'bg-[#002249] text-white border-[#002249]' : warn ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
    <div className={`text-[10.5px] uppercase tracking-wide font-semibold ${accent ? 'text-white/70' : 'text-slate-500'}`}>{label}</div>
    <div className={`text-base font-bold mt-0.5 ${accent ? 'text-white' : 'text-[#1a1a1a]'}`}>
      {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
    </div>
  </div>
);

const AddContactModal = ({ lists, onClose, onSaved }) => {
  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '', company: '',
    phone: '', city: '', state: '', country: '',
    tags: '', list_ids: [],
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.email.trim()) { toast.error('Email required'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      payload.tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      await axios.post(`${API}/admin/contacts`, payload, { withCredentials: true });
      toast.success('Contact saved');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add contact" onClose={onClose} testid="add-contact-modal">
      <div className="grid grid-cols-2 gap-2">
        <L label="Email *"><Input autoFocus value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} data-testid="add-contact-email" /></L>
        <L label="Company"><Input value={form.company} onChange={(e) => setForm(f => ({...f, company: e.target.value}))} /></L>
        <L label="First name"><Input value={form.first_name} onChange={(e) => setForm(f => ({...f, first_name: e.target.value}))} /></L>
        <L label="Last name"><Input value={form.last_name} onChange={(e) => setForm(f => ({...f, last_name: e.target.value}))} /></L>
        <L label="Phone"><Input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} /></L>
        <L label="City"><Input value={form.city} onChange={(e) => setForm(f => ({...f, city: e.target.value}))} /></L>
        <L label="State"><Input value={form.state} onChange={(e) => setForm(f => ({...f, state: e.target.value}))} /></L>
        <L label="Country"><Input value={form.country} onChange={(e) => setForm(f => ({...f, country: e.target.value}))} /></L>
      </div>
      <L label="Tags (comma-separated)"><Input value={form.tags} onChange={(e) => setForm(f => ({...f, tags: e.target.value}))} placeholder="orlando, sponsor, business-owner" /></L>
      <L label="Lists">
        <ListPicker lists={lists} value={form.list_ids} onChange={(v) => setForm(f => ({...f, list_ids: v}))} />
      </L>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="add-contact-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
          Save contact
        </Button>
      </div>
    </ModalShell>
  );
};

const BulkAddModal = ({ lists, onClose, onDone }) => {
  const [text, setText] = useState('');
  const [tags, setTags] = useState('');
  const [listIds, setListIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/contacts/bulk-add`, {
        emails: text,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        list_ids: listIds,
      }, { withCredentials: true });
      setResult(data);
      toast.success(`${data.created} added · ${data.updated} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk add failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add multiple contacts" onClose={onClose} testid="bulk-add-modal">
      <L label="Paste email addresses (comma, semicolon or newline separated)">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"alice@acme.com\nbob@example.com\ncarol@test.co"}
          rows={6}
          className="w-full border border-slate-200 rounded-md p-2 text-[13px] font-mono focus:border-[#002249] focus:outline-none"
          data-testid="bulk-add-text"
        />
      </L>
      <div className="grid grid-cols-2 gap-2">
        <L label="Tags"><Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="orlando, prospect" /></L>
        <L label="Lists"><ListPicker lists={lists} value={listIds} onChange={setListIds} /></L>
      </div>
      {result && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-[12px] grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          <ResultTile k="Detected" v={result.detected} />
          <ResultTile k="Valid" v={result.valid} />
          <ResultTile k="Created" v={result.created} tone="good" />
          <ResultTile k="Updated" v={result.updated} />
          <ResultTile k="Invalid" v={result.invalid?.length || 0} tone="warn" />
          <ResultTile k="File dupes" v={result.duplicates_in_input} />
          <ResultTile k="Suppressed" v={result.suppressed} tone="warn" />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        <Button onClick={result ? onDone : run} disabled={busy || (!text.trim() && !result)} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="bulk-add-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
          {result ? 'Done' : 'Add contacts'}
        </Button>
      </div>
    </ModalShell>
  );
};

const EditContactModal = ({ contact, lists, onClose, onSaved }) => {
  const [form, setForm] = useState({
    first_name: contact.first_name || '', last_name: contact.last_name || '',
    company: contact.company || '', phone: contact.phone || '',
    city: contact.city || '', state: contact.state || '',
    country: contact.country || '',
    tags: (contact.tags || []).join(', '),
    list_ids: contact.list_ids || [],
    status: contact.status || 'pending',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      payload.tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      await axios.patch(`${API}/admin/contacts/${contact.id}`, payload, { withCredentials: true });
      toast.success('Contact updated');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Edit ${contact.email}`} onClose={onClose} testid="edit-contact-modal">
      <div className="grid grid-cols-2 gap-2">
        <L label="First name"><Input value={form.first_name} onChange={(e) => setForm(f => ({...f, first_name: e.target.value}))} /></L>
        <L label="Last name"><Input value={form.last_name} onChange={(e) => setForm(f => ({...f, last_name: e.target.value}))} /></L>
        <L label="Company" full><Input value={form.company} onChange={(e) => setForm(f => ({...f, company: e.target.value}))} /></L>
        <L label="Phone"><Input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} /></L>
        <L label="City"><Input value={form.city} onChange={(e) => setForm(f => ({...f, city: e.target.value}))} /></L>
        <L label="State"><Input value={form.state} onChange={(e) => setForm(f => ({...f, state: e.target.value}))} /></L>
        <L label="Country"><Input value={form.country} onChange={(e) => setForm(f => ({...f, country: e.target.value}))} /></L>
      </div>
      <L label="Tags"><Input value={form.tags} onChange={(e) => setForm(f => ({...f, tags: e.target.value}))} /></L>
      <L label="Lists"><ListPicker lists={lists} value={form.list_ids} onChange={(v) => setForm(f => ({...f, list_ids: v}))} /></L>
      <L label="Status (Super Admin only)">
        <select value={form.status} onChange={(e) => setForm(f => ({...f, status: e.target.value}))} className="w-full h-9 border border-slate-200 rounded-md text-[12.5px] px-2 bg-white">
          <option value="subscribed">Subscribed</option>
          <option value="pending">Pending</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="suppressed">Suppressed</option>
          <option value="bounced">Bounced</option>
        </select>
      </L>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="edit-contact-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
          Save changes
        </Button>
      </div>
    </ModalShell>
  );
};

const ListPicker = ({ lists, value, onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {(lists || []).length === 0 && (
      <span className="text-[11px] text-slate-400">No lists yet — create one in the Lists tab.</span>
    )}
    {(lists || []).map((l) => {
      const active = value.includes(l.id);
      return (
        <button
          key={l.id}
          type="button"
          onClick={() => {
            onChange(active ? value.filter(x => x !== l.id) : [...value, l.id]);
          }}
          className={`px-2 py-1 rounded-md text-[11.5px] font-semibold ${active ? 'bg-[#002249] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
        >
          {active ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : null}
          {l.name}
        </button>
      );
    })}
  </div>
);

const ResultTile = ({ k, v, tone }) => (
  <div className={`rounded-md px-2 py-1 ${tone === 'good' ? 'bg-emerald-50 text-emerald-700' : tone === 'warn' ? 'bg-amber-50 text-amber-700' : 'bg-white text-slate-600'}`}>
    <div className="text-[9.5px] uppercase tracking-wide font-semibold opacity-80">{k}</div>
    <div className="text-sm font-bold">{v ?? 0}</div>
  </div>
);

const L = ({ label, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const ModalShell = ({ title, onClose, testid, children }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
    <div
      className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100 sticky top-0 bg-white z-10">
        <div className="font-semibold text-[13px] text-[#1a1a1a]">{title}</div>
        <button onClick={onClose} className="p-1 rounded-md text-slate-500 hover:bg-slate-50">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  </div>
);

export default ContactsPanel;
